
import React, { useEffect } from "react";
import { useLanguage } from "@/hooks/use-language";
import { useTournament } from "@/hooks/use-tournament";
import { TournamentSelector } from "./TournamentSelector";
import { MexicanoTournament } from "./MexicanoTournament";
import { AmericanoTournament } from "./AmericanoTournament";
import { TeamAmericanoIntegrated } from "./TeamAmericanoIntegrated";

interface TournamentManagerProps {
  participants: any[];
  initialFormat?: string;
  autoStart?: boolean;
  tournamentType?: 'americano' | 'mexicano' | 'team_americano';
}

export const TournamentManager: React.FC<TournamentManagerProps> = ({ 
  participants, 
  initialFormat,
  autoStart,
  tournamentType 
}) => {
  const { language } = useLanguage();
  
  // Add error boundary for tournament context
  let tournamentState;
  let resetTournament;
  let startTournament;
  
  try {
    const tournament = useTournament();
    tournamentState = tournament.tournamentState;
    resetTournament = tournament.resetTournament;
    startTournament = tournament.startTournament;
  } catch (error) {
    console.error('TournamentManager - Error accessing tournament context:', error);
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">
          {language === 'no' 
            ? 'Feil: Turneringskontekst ikke tilgjengelig' 
            : 'Error: Tournament context not available'}
        </p>
      </div>
    );
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('TournamentManager - tournamentState:', {
      isActive: tournamentState.isActive,
      currentRound: tournamentState.currentRound,
      tournamentType: tournamentState.tournamentType,
      isFinished: tournamentState.isFinished,
      isRoundActive: tournamentState.isRoundActive,
      hasMatches: tournamentState.americanoMatches?.length || 0,
      hasCourts: tournamentState.courts?.length || 0
    });
  }

  // Check if any tournament is active or has started
  const isTournamentActive = tournamentState.isActive || tournamentState.currentRound > 0 || tournamentState.isFinished;
  
  // Special handling for team_americano - show component even if not active
  const shouldShowTeamAmericano = tournamentState.tournamentType === 'team_americano';
  
  if (process.env.NODE_ENV === 'development') {
    console.log('TournamentManager - rendering decision:', {
      isTournamentActive,
      shouldShowTeamAmericano,
      tournamentType: tournamentState.tournamentType
    });
  }

  // Render active tournament based on type
  if ((isTournamentActive && tournamentState.tournamentType) || shouldShowTeamAmericano) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`Rendering ${tournamentState.tournamentType} tournament`);
    }
    
    if (tournamentState.tournamentType === 'mexicano') {
      return <MexicanoTournament participants={participants} />;
    } else if (tournamentState.tournamentType === 'americano') {
      return <AmericanoTournament participants={participants} />;
    } else if (tournamentState.tournamentType === 'team_americano') {
      return <TeamAmericanoIntegrated participants={participants} />;
    }
  }

  // Show tournament selector if no tournament is active
  if (process.env.NODE_ENV === 'development') {
    console.log('Rendering TournamentSelector');
  }
  return (
    <div>
      <TournamentSelector participants={participants} />
    </div>
  );
};
